import type { CSSProperties } from 'react';
import { IconBack } from './components/icons';

// Polish B2 — the Help page (/help), publicly readable (mirrors LegalPage's routing: no auth gate, reachable
// from the Landing footer or a direct link). A plain-language walkthrough, not a support ticket form — the
// CHANGELOG's outline, calm typography consistent with LegalPage.

interface Section { h: string; p: string }
const SECTIONS: Section[] = [
  {
    h: 'The two doors',
    // COPY CONTRACT (Sol-ratified 2026-08-07) — the old copy restated the retired landing hero ("see where it reads
    // strong…"), advertising a strength verdict the product does not issue. Replaced with Sol's exact wording.
    p: 'CVCraft has two doors from the home page. "Review your CV" gives you a free, specialty-aware read of a CV you already have — paste or import it and see what your CV already covers and where it looks thin. "Craft your CV" builds one with you, section by section, so you start from a clean structure instead of a blank page.',
  },
  {
    h: 'The writing rule',
    p: 'CVCraft never writes your CV for you. It asks questions and points out where a line reads as a duty instead of an impact, or where a claim needs a number — but every word on the page is yours. This is enforced on the server, not just in the interface.',
  },
  {
    h: 'What the evaluation measures',
    p: 'The quality read looks at section coverage (how much of your CV is represented) and evidence framing (whether your bullets show scope, outcomes, and specifics, not just duties). It does not assess specialty-specific clinical breadth or program fit — it is a read of common patterns in how CVs are written, not a prediction of match outcomes or a guarantee of anything.',
  },
  {
    h: 'Payments',
    p: 'Your first review is free. Unlocking a CV — for re-evaluation, crafting, tailoring to specific programs, and export — is a one-time payment (CA$19 for one CV, CA$49 for three), processed by LemonSqueezy as Merchant of Record. We never see your card details. See our Refund Policy for what happens if something goes wrong.',
  },
  {
    h: 'Copy-out and downloads',
    p: 'You can copy any section, entry, or your whole CV as clean plain text (no formatting baggage) at any time from the CV Workspace toolbar. You can also download a formatted .docx or PDF, with or without a "Crafted [date]" stamp, either as your canonical CV or tailored to a specific program.',
  },
  {
    h: 'Account deletion',
    p: 'You can delete your account from the Account page at any time. Deletion deactivates your account immediately with a 7-day sign-in recovery window, after which it is queued for permanent removal from active systems. See our Privacy Policy for the full retention details.',
  },
  {
    h: 'Contact',
    p: 'Questions, problems, or feedback — email support@cvcraft.ca.',
  },
];

export function Help({ onBack }: { onBack: () => void }) {
  return (
    <main style={S.main} data-testid="help-page">
      <button className="btn btn-ghost" style={S.back} onClick={onBack} data-testid="help-back"><IconBack aria-hidden="true" /> Back</button>
      <h1 style={S.h1}>Help</h1>
      <p style={S.intro}>A quick walkthrough of how CVCraft works.</p>
      {SECTIONS.map((s) => (
        <section key={s.h} style={S.section}>
          <h2 style={S.h2}>{s.h}</h2>
          <p style={S.p}>{s.p}</p>
        </section>
      ))}
    </main>
  );
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 680, margin: '40px auto', padding: '0 var(--space-4) 60px', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-prose)', color: 'var(--ink)' },
  back: { marginLeft: 0, paddingLeft: 0, fontSize: 'var(--fs-small)', marginBottom: 'var(--space-2)' },
  h1: { fontFamily: 'var(--serif)', fontSize: 'var(--fs-title)', margin: 'var(--space-2) 0 var(--space-1)' },
  intro: { fontSize: 15, color: 'var(--muted)', margin: '0 0 var(--space-5)' },
  section: { margin: 'var(--space-4) 0' },
  h2: { fontSize: 17, margin: '0 0 var(--space-2)' },
  p: { margin: 0, fontSize: 15, color: 'var(--ink-subtle)' },
};
